import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getShopifyCollections } from "@redotech/redo-merchant-app-common/client/shopify";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { isDef } from "@redotech/util/checks";
import { memo, useMemo, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";

export const CollectionSelectionModal = memo(function CollectionSelectionModal({
  selectedCollections,
  onCollectionsSelected,
  onClose,
}: {
  selectedCollections: string[];
  onCollectionsSelected: (collections: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const merchantClient = useRequiredContext(RedoMerchantClientContext);

  const [localSelectedCollections, setLocalSelectedCollections] =
    useState<string[]>(selectedCollections);
  const [searchString, setSearchString] = useState("");

  const debouncedSearch = useDebounce(searchString, 500);

  const collectionsLoad = useLoad(async () => {
    const collections = await getShopifyCollections(merchantClient, {
      searchString: debouncedSearch,
    });
    return collections.map((collection) => ({
      id: convertShopifyIdToGid(collection.id, ShopifyIdType.Collection),
      title: collection.name,
    }));
  }, [debouncedSearch, merchantClient]);

  // Cache of all seen collections so selected items retain their title even
  // when they're no longer in the current search results.
  const knownCollectionsRef = useRef<Map<string, string>>(new Map());

  const collectionOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    for (const collection of collectionsLoad.value ?? []) {
      if (collection) {
        knownCollectionsRef.current.set(collection.id, collection.title);
      }
    }
    const loadedIds = new Set<string>(
      collectionsLoad.value?.filter(isDef).map((c) => c.id) ?? [],
    );
    const searchResults: BadgeRedoListItem<string>[] =
      collectionsLoad.value
        ?.filter(isDef)
        .map((collection) => ({
          type: "badge",
          id: collection.id,
          value: collection.id,
          badge: { text: collection.title, color: "gray" },
        })) ?? [];

    // Include selected collections that aren't in the current search results
    // so they remain visible and checked in the dropdown.
    const selectedNotInResults = localSelectedCollections
      .filter((id) => !loadedIds.has(id))
      .map<
        BadgeRedoListItem<string>
      >((id) => ({ type: "badge", id, value: id, badge: { text: knownCollectionsRef.current.get(id) ?? id, color: "gray" } }));

    return [...searchResults, ...selectedNotInResults];
  }, [collectionsLoad.value, localSelectedCollections]);

  const selectedOptions = useMemo(() => {
    return collectionOptions.filter((option) =>
      localSelectedCollections.includes(option.value),
    );
  }, [collectionOptions, localSelectedCollections]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setLocalSelectedCollections(items.map((item) => item.value));
    },
  );

  const handleSave = useHandler(() => {
    onCollectionsSelected(localSelectedCollections);
  });

  return (
    <RedoModal
      modalSize="md"
      onModalCloseRequested={onClose}
      primaryButton={{ text: t("Save"), onClick: handleSave }}
      secondaryButton={{ text: t("Cancel"), onClick: onClose }}
      title={t("Select Collections")}
    >
      <Flex dir="column" gap="lg" p="md">
        <RedoTagsDropdownInput<string>
          label={t("Collections")}
          loading={collectionsLoad.pending}
          options={collectionOptions}
          placeholder={t("Select collections")}
          searchPlaceholder={t("Search for collections")}
          searchString={searchString}
          selectedOptions={selectedOptions}
          setSearchString={setSearchString}
          setSelectedOptions={handleSelectionChange}
          size="xs"
        />
      </Flex>
    </RedoModal>
  );
});
